import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { useBranding } from '@/lib/branding/BrandingProvider';

export interface DashboardStats {
  employees: number;
  endClients: number;
  activeLicenses: number;
  interviewsThisMonth: number;
  assessmentsThisMonth: number;
  quota: { used: number; total: number; percent: number };
}

async function countWhere(table: string, column: string, value: string): Promise<number> {
  const { count, error } = await supabase
    .from(table as never)
    .select('*', { count: 'exact', head: true })
    .eq(column as never, value as never);
  if (error) throw new Error(`${table}: ${error.message}`);
  return count ?? 0;
}

async function fetchStats(companyId: string): Promise<DashboardStats> {
  // License counts (active seats) come from one RPC that resolves company
  // linkage correctly (direct or via end client) and excludes employee_demo
  // seats — the naive neurax_licenses count both under-counted
  // end-client-linked licenses and over-counted demos.
  const [employees, endClients, companyRow, licenseRes, sessionRes, usageRes] = await Promise.all([
    countWhere('b2b_employees', 'b2b_company_id', companyId),
    countWhere('b2b_end_clients', 'b2b_company_id', companyId),
    supabase.from('b2b_companies').select('monthly_quota').eq('id', companyId).single(),
    supabase.rpc('get_b2b_license_counts' as never, { p_company_id: companyId } as never),
    // Monthly interview/assessment usage counts (current month). Usage metric,
    // not filtered by is_billable; resolves company directly or via end client.
    supabase.rpc('get_b2b_monthly_session_counts' as never, { p_company_id: companyId } as never),
    // Quota meter: billable, non-waived setups this month — the billing unit
    // (one charge per confirmed setup), same number the Billing statement shows.
    supabase.rpc('get_b2b_monthly_usage' as never, { p_company_id: companyId } as never),
  ]);

  if (companyRow.error || !companyRow.data) {
    throw new Error(`b2b_companies: ${companyRow.error?.message ?? 'not found'}`);
  }
  if (licenseRes.error) {
    throw new Error(`get_b2b_license_counts: ${licenseRes.error.message}`);
  }
  if (sessionRes.error) {
    throw new Error(`get_b2b_monthly_session_counts: ${sessionRes.error.message}`);
  }
  if (usageRes.error) {
    throw new Error(`get_b2b_monthly_usage: ${usageRes.error.message}`);
  }

  const counts = (licenseRes.data as { issued_this_month?: number; active?: number } | null) ?? {};
  const sessionCounts =
    (sessionRes.data as { interviews?: number; assessments?: number } | null) ?? {};
  const total = companyRow.data.monthly_quota ?? 0;
  // "Used" against the plan = billable setups this month (the billing unit).
  const used = (usageRes.data as number | null) ?? 0;
  const percent = total > 0 ? Math.round((used / total) * 100) : 0;

  return {
    employees,
    endClients,
    activeLicenses: counts.active ?? 0,
    interviewsThisMonth: sessionCounts.interviews ?? 0,
    assessmentsThisMonth: sessionCounts.assessments ?? 0,
    quota: { used, total, percent },
  };
}

export function useDashboardStats() {
  const { companyId } = useBranding();
  return useQuery({
    queryKey: ['neurax', 'dashboard-stats', companyId],
    queryFn: () => fetchStats(companyId),
    staleTime: 30_000,
  });
}
